import React, { useState } from 'react'
import { connect } from 'react-redux'
import {
  Alert,
  Button,
  Form,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import AlertMessage from '../../components/AlertMessage'
import integrationsActions from '../../integrations/actions'
import PageLoader from '../../components/PageLoader'
import projectsActions from './actions'

const LinkToIntegrationModal = ({
  bulkLinkError = null,
  bulkLinkResults = null,
  bulkLinkStatus = 'idle',
  dispatch,
  integrations = [],
  isOpen,
  onClose,
  projects = [],
  selectedProjectIds
}) => {
  const { t } = useTranslation(['ProjectsPage', 'commons'])
  const [selectedIntegration, setSelectedIntegration] = useState(null)

  const selectedProjects = (projects || []).filter(
    (p) => selectedProjectIds.includes(p.id)
  )

  const integrationOptions = (integrations || []).map((integration) => ({
    value: integration.id,
    label: `${integration.platform} - ${integration.url}`,
    integration
  }))

  const handleSubmit = (event) => {
    event.preventDefault()

    if (!selectedIntegration) return

    const projectsToLink = selectedProjects.map((project) => ({
      project_id: project.id
      // repository_id would be added here if URL differs
    }))

    dispatch(projectsActions.bulkLink(selectedIntegration.value, projectsToLink))
  }

  const handleClose = () => {
    // Refetch integrations to update projects_count
    if (bulkLinkStatus === 'succeeded') {
      dispatch(integrationsActions.fetchAll())
    }
    setSelectedIntegration(null)
    dispatch(projectsActions.resetBulkLink())
    onClose()
  }

  const renderResults = () => {
    if (!bulkLinkResults) return null

    const successCount = bulkLinkResults.filter((r) => r.success).length
    const failedResults = bulkLinkResults.filter((r) => !r.success)

    return (
      <div className="mt-3">
        <Alert color={failedResults.length > 0 ? 'warning' : 'success'}>
          {t('LinkToIntegrationModal.resultsMessage', {
            success: successCount,
            total: bulkLinkResults.length
          })}
        </Alert>
        {failedResults.length > 0 && (
          <ListGroup>
            {failedResults.map((result) => {
              const project = projects.find((p) => p.id === result.project_id)
              return (
                <ListGroupItem key={result.project_id} color="danger">
                  <strong>{project?.name || result.project_id}</strong>
                  {': '}
                  {typeof result.errors === 'string'
                    ? result.errors
                    : JSON.stringify(result.errors)}
                </ListGroupItem>
              )
            })}
          </ListGroup>
        )}
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={handleClose}>
          {t('LinkToIntegrationModal.title')}
        </ModalHeader>
        <ModalBody>
          {bulkLinkStatus === 'idle' && (
            <>
              <p>
                {t('LinkToIntegrationModal.description', {
                  count: selectedProjects.length
                })}
              </p>

              <ListGroup className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {selectedProjects.map((project) => (
                  <ListGroupItem key={project.id}>
                    {project.name}
                  </ListGroupItem>
                ))}
              </ListGroup>

              <div className="mb-3">
                <label className="form-label">
                  {t('LinkToIntegrationModal.selectIntegration')}
                </label>
                <Select
                  isClearable
                  onChange={setSelectedIntegration}
                  options={integrationOptions}
                  placeholder={t('LinkToIntegrationModal.selectPlaceholder')}
                  value={selectedIntegration}
                />
              </div>

              {selectedIntegration && (
                <Alert color="info">
                  {t('LinkToIntegrationModal.willLinkTo', {
                    platform: selectedIntegration.integration.platform,
                    url: selectedIntegration.integration.url
                  })}
                </Alert>
              )}
            </>
          )}

          {bulkLinkStatus === 'linking' && (
            <PageLoader message={t('LinkToIntegrationModal.linking')} />
          )}

          {bulkLinkStatus === 'failed' && (
            <AlertMessage>{bulkLinkError}</AlertMessage>
          )}

          {bulkLinkStatus === 'succeeded' && renderResults()}
        </ModalBody>
        <ModalFooter>
          {bulkLinkStatus === 'idle' && (
            <>
              <Button color="secondary" onClick={handleClose}>
                {t('buttons.cancel', { ns: 'commons' })}
              </Button>
              <Button
                color="primary"
                disabled={!selectedIntegration}
                type="submit"
              >
                {t('LinkToIntegrationModal.linkButton')}
              </Button>
            </>
          )}

          {(bulkLinkStatus === 'succeeded' || bulkLinkStatus === 'failed') && (
            <Button color="primary" onClick={handleClose}>
              {t('buttons.close', { ns: 'commons' })}
            </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  )
}

LinkToIntegrationModal.propTypes = {
  bulkLinkError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  bulkLinkResults: PropTypes.arrayOf(PropTypes.shape({
    project_id: PropTypes.string.isRequired,
    success: PropTypes.bool.isRequired,
    errors: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  })),
  bulkLinkStatus: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  integrations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  })),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projects: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  })),
  selectedProjectIds: PropTypes.arrayOf(PropTypes.string).isRequired
}

const mapStateToProps = ({
  projects: {
    bulkLinkError,
    bulkLinkResults,
    bulkLinkStatus
  }
}) => ({
  bulkLinkError,
  bulkLinkResults,
  bulkLinkStatus
})

export default connect(mapStateToProps)(LinkToIntegrationModal)
